import { useState, useMemo, useRef, useEffect, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Clock, Volume2, VolumeX, Settings, BookOpen, ChevronRight } from "lucide-react";
import CompassLogo from "@/components/CompassLogo";
import EuropeMap from "@/components/EuropeMap";
import Timeline from "@/components/Timeline";
import { chronicles, Chronicle } from "@/data/chronicles";
import { useAchievements } from "@/hooks/useAchievements";
import StartScreen from "@/pages/StartScreen";
import { hasCompletedTutorial } from "@/components/HowToPlayTutorial";
import { TUTORIAL_FINISH_EVENT, isTutorialActive, TUTORIAL_HOME_VIEW_EVENT, TUTORIAL_START_EVENT, type TutorialHomeView } from "@/components/tutorial/tutorial-session";
import DisclosuresModal from "@/components/DisclosuresModal";
import { FEEDBACK_OPEN_EVENT } from "@/lib/feedbackEvents";
import { hasAckedDisclosures, DISCLOSURES_REOPEN_EVENT } from "@/lib/disclosures";
import { SETTINGS_OPEN_EVENT, type SettingsFocus, type SettingsOpenDetail } from "@/lib/settingsEvents";
import { BrassButton, GoldRule } from "@/components/atlas/AtlasComponents";
import { useSettings } from "@/engine/settingsStore";
import { trackVolume } from "@/components/game/AmbientMusic";
import { RouteHead } from "@/components/RouteHead";
// Lazy-loaded modal panels — these only mount after a user click on the
// header buttons (Achievements / Ledger / Settings). Pulling them out of
// the eager import set drops the home-page initial bundle by ~150-200 KB.
// Each first-open pays a one-frame chunk fetch; Vite caches it after that.
const AchievementShowcase = lazy(() => import("@/components/AchievementShowcase"));
const ArchivistLedger     = lazy(() => import("@/components/ArchivistLedger"));
const SettingsPanel       = lazy(() => import("@/components/SettingsPanel"));
// The feedback form carries zod; load it only when a player opens it.
const SendFeedbackModal   = lazy(() => import("@/components/SendFeedbackModal"));
import { useIsMobile } from "@/hooks/use-mobile";
import { useLastPlayedChronicle } from "@/hooks/useLastPlayedChronicle";


// Loudness-normalised playback level for the home-map ambient, resolved
// from the SAME per-track gain table as every chronicle track (see
// trackVolume / TRACK_GAIN in AmbientMusic.tsx) so the map is perceptually
// level-matched to the rest of the game instead of a flat base volume.
const MAP_MUSIC_VOLUME = trackVolume("/audio/map-ambient.mp3");


const Index = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { settings, update } = useSettings();
  
  const [showStart, setShowStart] = useState(() => !sessionStorage.getItem("hasEnteredMap"));
  // "Good to know" popup for returning players: someone who finished the
  // tour but has not acknowledged the current version of the notes (for
  // example after the version bump for the analytics note).
  const [showDisclosures, setShowDisclosures] = useState(() => !hasAckedDisclosures() && hasCompletedTutorial());
  // New players: the same notes right after the welcome screen, before the
  // tour starts, so the dark-themes and analytics notes come before play.
  const [preTourNotes, setPreTourNotes] = useState(false);
  // Separate state for the Settings → Good to Know path. Uses
  // review mode (no ack flag rewritten) and renders even when the
  // player has already acknowledged. Kept separate from showDisclosures
  // so the first-run flow stays untouched.
  const [showDisclosuresReview, setShowDisclosuresReview] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  // Listen for the "Send Feedback" trigger from Settings.
  useEffect(() => {
    const handler = () => setShowFeedback(true);
    window.addEventListener(FEEDBACK_OPEN_EVENT, handler);
    return () => window.removeEventListener(FEEDBACK_OPEN_EVENT, handler);
  }, []);

  // Listen for the "Replay tutorial" trigger from Settings → so the
  // tutorial opens immediately instead of waiting for the next
  // session (the StartScreen path is the only thing that used to
  // re-fire it, which never ran a second time in the same tab).
  useEffect(() => {
    const handler = (event: Event) => {
      const view = (event as CustomEvent<TutorialHomeView>).detail;
      setTimelineOpen(view === "timeline");
      setAchievementsOpen(view === "achievements");
      setSettingsOpen(view === "settings");
      setLedgerOpen(view === "ledger");
    };
    window.addEventListener(TUTORIAL_HOME_VIEW_EVENT, handler);
    return () => window.removeEventListener(TUTORIAL_HOME_VIEW_EVENT, handler);
  }, []);

  // Same pattern for Good to Know from Settings.
  useEffect(() => {
    const handler = () => setShowDisclosuresReview(true);
    window.addEventListener(DISCLOSURES_REOPEN_EVENT, handler);
    return () => window.removeEventListener(DISCLOSURES_REOPEN_EVENT, handler);
  }, []);

  // Defensive: if Index mounts and the player hasn't completed the
  // tutorial AND has already entered the map this session (e.g. they
  // navigated home from a chronicle without ever seeing the start
  // screen), open the tutorial anyway. The original gating only ran
  // through StartScreen's onEnter callback, so deep-links straight to
  // the map could skip it entirely.
  useEffect(() => {
    if (!showStart && !hasCompletedTutorial() && !isTutorialActive()) {
      if (!hasAckedDisclosures()) { setPreTourNotes(true); return; }
      const timer = window.setTimeout(() => window.dispatchEvent(new CustomEvent(TUTORIAL_START_EVENT)), 0);
      return () => window.clearTimeout(timer);
    }
  }, [showStart]);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [achievementsOpen, setAchievementsOpen] = useState(false);
  const [timeRange] = useState<[number, number]>([476, 2026]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsFocus, setSettingsFocus] = useState<SettingsFocus | undefined>(undefined);
  // "Turn it off in Settings" in the analytics note, and anything else that
  // wants Settings opened on a particular control.
  useEffect(() => {
    const handler = (e: Event) => {
      setSettingsFocus((e as CustomEvent<SettingsOpenDetail>).detail?.focus);
      setSettingsOpen(true);
    };
    window.addEventListener(SETTINGS_OPEN_EVENT, handler);
    return () => window.removeEventListener(SETTINGS_OPEN_EVENT, handler);
  }, []);
  const [ledgerOpen, setLedgerOpen] = useState(false);

  // Bidirectional hover sync between the map's region pins and the timeline's
  // chronicle bars. Hovering either highlights the related items on the other,
  // so the player feels the map and timeline as a single chart instead of two
  // disconnected views. Disabled on touch devices (no real hover, and the
  // timeline panel covers the map anyway).
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const { achievementProgress, unseenUnlockCount, markAllSeen } = useAchievements();
  const lastPlayed = useLastPlayedChronicle();


  // Map ambient music. Three behaviours folded in here:
  //   1. Auto-start on first user interaction. The `audio.play()` call
  //      will be rejected by browser autoplay policy if it fires
  //      before any user gesture. We attach one-shot click/keydown/
  //      touchstart listeners that retry play() on the first
  //      interaction, then remove themselves. (The StartScreen click
  //      already counts as one such gesture, so on most paths the
  //      first try succeeds.)
  //   2. Pause on page hidden. When the user backgrounds the tab,
  //      switches apps, or locks the phone, document.visibilityState
  //      becomes "hidden" — pause the music so it doesn't keep
  //      playing in the background. On return (visible) the music
  //      resumes if not muted.
  //   3. Honour mute / volume changes from the settings store.
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // True while the map music is fading in, so the master-volume sync effect
  // below doesn't snap it to full mid-fade (mirrors the chronicle ambient's
  // moduleFadeInProgress guard in AmbientMusic.tsx).
  const mapFadingRef = useRef(false);
  // Mirror musicMuted into a ref so the visibility and autoplay-retry
  // handlers below (registered once per mount, deps []) always read
  // the LIVE mute state. Without this, toggling mute then switching
  // away and returning would replay the music — the handler would
  // still close over the first-render value of settings.musicMuted.
  const mutedRef = useRef(settings.musicMuted);
  useEffect(() => { mutedRef.current = settings.musicMuted; }, [settings.musicMuted]);

  useEffect(() => {
    const audio = new Audio("/audio/map-ambient.mp3");
    audio.loop = true;
    audio.volume = 0; // start silent; fade in on first play (uniform with the chronicle ambient)
    audio.preload = "auto";
    audioRef.current = audio;

    let played = false;
    let cancelled = false;

    const tryPlay = () => {
      if (played || cancelled) return;
      // Use the live mute ref, not the captured-at-mount value, so a
      // user who muted on the chronicle screen returns to the map
      // without the autoplay-retry firing on their next click.
      if (mutedRef.current) return;
      if (document.visibilityState === "hidden") return;
      audio.play().then(() => {
        if (cancelled) return;
        played = true;
        document.removeEventListener("click", tryPlay);
        document.removeEventListener("keydown", tryPlay);
        document.removeEventListener("touchstart", tryPlay);
        // Fade in over 1.5s (30 × 50ms) to match the chronicle ambient track,
        // so music fades in uniformly everywhere instead of the map snapping
        // straight to full volume on the first gesture.
        const target = MAP_MUSIC_VOLUME * (settings.masterVolume / 100);
        mapFadingRef.current = true;
        let s = 0;
        const iv = setInterval(() => {
          s++;
          audio.volume = Math.max(0, Math.min(1, target * (s / 30)));
          if (s >= 30) { clearInterval(iv); audio.volume = Math.max(0, Math.min(1, target)); mapFadingRef.current = false; }
        }, 50);
      }).catch(() => {});
    };

    tryPlay();
    document.addEventListener("click", tryPlay);
    document.addEventListener("keydown", tryPlay);
    document.addEventListener("touchstart", tryPlay);

    // Visibility handling — pause when the page is backgrounded,
    // resume on return only if NOT muted (read via ref to avoid the
    // stale-closure replay-after-mute bug).
    const onVisibility = () => {
      if (!audioRef.current) return;
      if (document.visibilityState === "hidden") {
        audioRef.current.pause();
      } else if (!mutedRef.current) {
        audioRef.current.play().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("click", tryPlay);
      document.removeEventListener("keydown", tryPlay);
      document.removeEventListener("touchstart", tryPlay);
      document.removeEventListener("visibilitychange", onVisibility);
      audio.pause();
      audio.src = "";
      if (audioRef.current === audio) audioRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (audioRef.current && !mapFadingRef.current) {
      audioRef.current.volume = MAP_MUSIC_VOLUME * (settings.masterVolume / 100);
    }
  }, [settings.masterVolume]);

  useEffect(() => {
    if (!audioRef.current) return;
    if (settings.musicMuted) {
      audioRef.current.pause();
    } else if (document.visibilityState === "visible") {
      audioRef.current.play().catch(() => {});
    }
  }, [settings.musicMuted]);

  const filteredChronicles = useMemo(() => {
    return chronicles
      .filter((c) => c.dateRange[0] <= timeRange[1] && c.dateRange[1] >= timeRange[0])
      .sort((a, b) => a.dateRange[0] - b.dateRange[0]);
  }, [timeRange]);

  const handleChronicleSelect = (chronicle: Chronicle) => {
    navigate(`/chronicle/${chronicle.id}`);
  };

  useEffect(() => {
    // The tour's last card is its send-off: finishing just clears the map.
    const finish = () => {
      setTimelineOpen(false); setAchievementsOpen(false); setSettingsOpen(false); setLedgerOpen(false);
      setShowStart(false);
    };
    window.addEventListener(TUTORIAL_FINISH_EVENT, finish);
    return () => window.removeEventListener(TUTORIAL_FINISH_EVENT, finish);
  }, []);

  if (showStart) {
    return <StartScreen onEnter={() => {
      sessionStorage.setItem("hasEnteredMap", "1");
      // Open the tutorial only on the player's first ever visit
      // (localStorage gate, not sessionStorage). Settings → Replay
      // tutorial clears the flag for returning players who want a refresher.
      // A new player reads the "Good to know" notes first; the tour starts
      // from their "Start the tour" button.
      if (!hasCompletedTutorial() && !isTutorialActive()) {
        if (!hasAckedDisclosures()) setPreTourNotes(true);
        else window.setTimeout(() => window.dispatchEvent(new CustomEvent(TUTORIAL_START_EVENT)), 0);
      }
      setShowStart(false);
    }} />;
  }

  return (
    <div className="h-screen bg-background grain-overlay flex flex-col overflow-hidden">
      <RouteHead
        title="Chronicles of Europe: Interactive Historical Stories"
        description="A cartographer's map of Europe and a thousand years of timelines. Pick a chronicle and live history through one ordinary person's eyes."
        path="/"
      />
      {/* Visually hidden page heading — gives the map view a real <h1>
          for screen readers and SEO without disturbing the cartography. */}
      <h1 className="sr-only">Chronicle Map | Chronicles of Europe</h1>
      {/* First-launch order: welcome screen, the tour (skippable), then the
          closing screen with the "Good to know" notes, then the map. The
          popup below only appears for returning players who have not seen
          the current notes, and from Settings. */}
      {preTourNotes && (
        <DisclosuresModal
          mode="firstRun"
          ctaLabel="Start the tour"
          onDismiss={() => {
            setPreTourNotes(false);
            window.setTimeout(() => window.dispatchEvent(new CustomEvent(TUTORIAL_START_EVENT)), 0);
          }}
        />
      )}
      {showDisclosures && !preTourNotes && (
        <DisclosuresModal
          mode="firstRun"
          onDismiss={() => setShowDisclosures(false)}
        />
      )}
      {showDisclosuresReview && !showDisclosures && (
        <DisclosuresModal
          mode="review"
          onDismiss={() => setShowDisclosuresReview(false)}
        />
      )}
      {showFeedback && (
        <Suspense fallback={null}>
          <SendFeedbackModal onDismiss={() => setShowFeedback(false)} />
        </Suspense>
      )}


      {/* Header — tightened vertical padding on small viewports so the
          map (object-contain at default, often letterboxed on portrait
          phones) gets a few more pixels of vertical room. The 44 px
          touch-target minimum on the buttons inside is doing the real
          height work; reducing py from 2 → 1 on mobile claws back ~8 px
          for the desk + map area without compromising tap accuracy. */}
      <header className="relative z-30 px-3 sm:px-6 py-1 sm:py-3 flex items-center justify-between border-b border-border/20 bg-background/80 backdrop-blur-sm">
        {/* Logo / title — full "Chronicles of Europe" only at lg: (≥1024 px),
            so the five-button cluster on the right has room to breathe on
            tablets. Mobile + tablet (< 1024) shows the compact "CoE" mark. */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => { sessionStorage.removeItem("hasEnteredMap"); setShowStart(true); }}
          className="flex items-center gap-2 font-display text-sm lg:text-lg text-gold gold-text-glow tracking-[0.1em] lg:tracking-[0.15em] bg-transparent border-none cursor-pointer min-w-0 overflow-hidden"
        >
          <CompassLogo size="w-6 h-6 md:w-7 md:h-7" className="gold-glow flex-shrink-0" />
          {/* Full wordmark on sm+ (iPhone Pro Max landscape, all iPads, desktops),
              hidden again in the awkward 1024–1279 window where the right-side
              buttons gain text labels and would otherwise crowd the title,
              then restored at xl: where there's comfortably room. */}
          <span className="hidden sm:inline lg:hidden min-[1220px]:inline truncate">Chronicles of Europe</span>
          <span className="sm:hidden lg:inline min-[1220px]:hidden">CoE</span>
        </motion.button>

        {/* Controls — timeline, achievements, ledger, sound, settings.
            All five buttons share one engraved-brass treatment via
            <BrassButton /> — see src/components/atlas/AtlasComponents.tsx
            for the active/inactive + nav/compact size variants. */}
        <div className="flex items-center gap-1.5 sm:gap-2" data-tutorial="home-tools">
          {/* Timeline */}
          <BrassButton
            data-tutorial="home-timeline-btn"
            onClick={() => setTimelineOpen(!timelineOpen)}
            active={timelineOpen}
            icon={<Clock className="w-3.5 h-3.5" />}
            aria-label={timelineOpen ? "Close timeline" : "Explore timeline"}
          >
            {timelineOpen ? "Close Timeline" : "Explore Timeline"}
          </BrassButton>

          {/* Achievements — badge shows count of unlocked-but-unseen
              achievements so the player has a cue when something new is
              waiting in the showcase. Cleared when they open it. */}
          <BrassButton
            data-tutorial="home-achievements-btn"
            onClick={() => { markAllSeen(); setAchievementsOpen(true); }}
            icon={<Trophy className="w-3.5 h-3.5" />}
            className="relative"
            aria-label="Open achievements"
          >
            Achievements
            {unseenUnlockCount > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-display font-semibold flex items-center justify-center gold-glow"
                aria-label={`${unseenUnlockCount} new achievement${unseenUnlockCount > 1 ? "s" : ""}`}
              >
                {unseenUnlockCount}
              </span>
            )}
          </BrassButton>

          {/* Archivist's Ledger */}
          <BrassButton
            data-tutorial="home-ledger-btn"
            onClick={() => setLedgerOpen(true)}
            icon={<BookOpen className="w-3.5 h-3.5" />}
            aria-label="Open Archivist's Ledger"
          >
            Ledger
          </BrassButton>

          {/* Divider — appears with the labels at lg+ so the icon-only
              tablet header keeps the row visually unbroken. */}
          <div className="hidden lg:block w-px h-5 bg-border/30 mx-0.5" />

          {/* Music toggle */}
          <BrassButton
            size="compact"
            active={!settings.musicMuted}
            onClick={() => update({ musicMuted: !settings.musicMuted })}
            title={settings.musicMuted ? "Unmute music" : "Mute music"}
            aria-label={settings.musicMuted ? "Unmute music" : "Mute music"}
            icon={settings.musicMuted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
          />

          {/* Settings */}
          <BrassButton
            size="compact"
            data-tutorial="home-settings-btn"
            onClick={() => setSettingsOpen(true)}
            title="Settings"
            aria-label="Open settings"
            icon={<Settings className="w-3.5 h-3.5" />}
          />
        </div>
      </header>


      {/* Map fills remaining space — sits on the cartographer's desk surface
          (woven dark texture). The same `.atlas-desk` utility wraps Play.tsx
          so the metaphor of "you are reading at a desk" continues across the
          home view and into chronicles. */}
      {/* Full-bleed map. The .atlas-desk class stays on <main> so the
          weave shows during the brief load before the map paints, but
          there's no size cap or centering — the map fills the available
          space. */}
      <main className="relative flex-1 min-h-0 atlas-desk">
        {/* Tagline */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="absolute top-3 left-3 sm:top-3 sm:left-4 z-20 pointer-events-none"
        >
          <p className="text-xs font-body italic text-foreground/30 tracking-wide drop-shadow-lg">
            "A life in a continent on the rise."
          </p>
        </motion.div>

        {/* Continue / last-played banner.
            In-progress save → clickable "Continue" shortcut into Play.
            Completed save   → non-clickable "Last played" label. We deliberately
            don't link completed runs back into Play, because that would let
            players skip the whole story and just rerun the final choice node
            to unlock alternative endings. To explore other endings, players
            must restart from the chronicle detail screen. */}
        <AnimatePresence>
          {lastPlayed && !lastPlayed.isComplete && (
            <motion.button
              key="continue"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4, delay: 0.5 }}
              onClick={() => navigate(`/chronicle/${lastPlayed.chronicleId}/play`)}
              // Phone: full-width centred chip just below the header (unchanged).
              // Desktop (sm+): pinned top-right of the map area, capped width
              // so it never overflows the viewport edge regardless of how
              // long the chronicle title is. left-auto / mx-0 / w-fit on sm
              // override the phone-side stretch rules.
              className="absolute top-2 right-2 sm:top-3 sm:right-4 z-20 group flex items-center gap-2 sm:gap-3 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-sm border border-primary/30 bg-card/80 backdrop-blur-sm hover:border-primary/60 hover:bg-card/95 transition-all w-fit max-w-[calc(100vw-1rem)] sm:max-w-[min(360px,calc(100vw-2rem))]"
              style={{ boxShadow: "0 4px 20px hsl(0 0% 0% / 0.4), 0 0 12px hsl(43 80% 65% / 0.05)" }}
            >
              {/* Mobile: tiny chevron-right "play" glyph instead of the "Continue" word */}
              <ChevronRight className="w-3.5 h-3.5 text-primary/80 sm:hidden flex-shrink-0" />
              <span className="text-[9px] font-display uppercase tracking-[0.25em] text-primary/80 hidden sm:inline">
                Continue
              </span>
              <span className="hidden sm:block w-px h-4 bg-border/40" />
              <div className="text-left min-w-0 flex-1">
                <p className="font-display text-[10px] sm:text-xs text-foreground/90 truncate leading-tight">
                  {lastPlayed.chronicleTitle}
                </p>
                {/* Phone subtitle: drop season/year, only show decisions count */}
                <p className="text-[8px] sm:text-[10px] font-body text-muted-foreground/60 truncate leading-tight sm:hidden">
                  {lastPlayed.decisionsMade} decision{lastPlayed.decisionsMade === 1 ? "" : "s"}
                </p>
                <p className="text-[10px] font-body text-muted-foreground/60 truncate hidden sm:block">
                  {lastPlayed.characterName} · {lastPlayed.season} {lastPlayed.year} · {lastPlayed.decisionsMade} decision{lastPlayed.decisionsMade === 1 ? "" : "s"}
                </p>
              </div>
              <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary/70 group-hover:text-primary group-hover:translate-x-0.5 transition-all flex-shrink-0" />
            </motion.button>
          )}
          {lastPlayed && lastPlayed.isComplete && (
            <motion.div
              key="lastplayed"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4, delay: 0.5 }}
              // Phone: full-width centred chip just below header (unchanged).
              // Desktop: pinned top-right of the map area with width cap so it
              // never overflows the viewport.
              className="absolute top-2 right-2 sm:top-3 sm:right-4 z-20 flex items-center gap-2 sm:gap-3 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-sm border border-border/40 bg-card/60 backdrop-blur-sm w-fit max-w-[calc(100vw-1rem)] sm:max-w-[min(360px,calc(100vw-2rem))] pointer-events-none"
              title="Open the chronicle from the map to start a new playthrough."
            >
              <span className="text-[8px] font-display uppercase tracking-[0.2em] text-muted-foreground/60 sm:hidden flex-shrink-0">
                ✦
              </span>
              <span className="text-[9px] font-display uppercase tracking-[0.25em] text-muted-foreground/70 hidden sm:inline">
                Last Played
              </span>
              <span className="hidden sm:block w-px h-4 bg-border/40" />
              <div className="text-left min-w-0 flex-1">
                <p className="font-display text-[10px] sm:text-xs text-foreground/70 truncate leading-tight">
                  {lastPlayed.chronicleTitle}
                </p>
                <p className="text-[8px] sm:text-[10px] font-body text-muted-foreground/50 truncate leading-tight sm:hidden">
                  Last played · {lastPlayed.decisionsMade} decision{lastPlayed.decisionsMade === 1 ? "" : "s"}
                </p>
                <p className="text-[10px] font-body text-muted-foreground/50 truncate hidden sm:block">
                  Completed · {lastPlayed.characterName} · {lastPlayed.decisionsMade} decision{lastPlayed.decisionsMade === 1 ? "" : "s"}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* The map is a chart laid on the desk — padding around the
            container leaves a strip of woven desk visible on every side
            so the metaphor reads at a glance. The map itself wears a thin
            brass-dim border + drop shadow so it sits proud of the desk
            surface like a sheet of paper.
            Mobile uses a tighter inset (the desk is barely visible there
            but the strip is enough to register); lg+ gets a more generous
            margin so the desk strip is unmistakeable. */}
        {/* Full-bleed map — fills the entire main area. The vignette
            and grain overlay live inside EuropeMap itself, so we don't
            need a bordered wrapper here. */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="relative w-full h-full"
          data-tutorial="home-map"
        >
          <EuropeMap
            filteredChronicles={filteredChronicles}
            onRegionHoverChange={isMobile ? undefined : setHoveredRegion}
          />
        </motion.div>
      </main>

      {/* Timeline panel */}
      <AnimatePresence>
        {timelineOpen && (
          <>
            {isMobile ? (
              <>
                <motion.div data-tutorial="timeline-panel"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-[150] bg-black/60"
                  onClick={() => setTimelineOpen(false)}
                />
                <motion.div
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ type: "spring", damping: 30, stiffness: 300 }}
                  className="fixed bottom-0 left-0 right-0 z-[151] rounded-t-lg overflow-hidden max-h-[70vh] supports-[height:100dvh]:max-h-[70dvh]"
                  style={{
                    background: "var(--surface-parchment-elevated)",
                    borderTop: "1px solid hsl(43, 50%, 35% / 0.4)",
                    boxShadow: "0 -10px 30px hsl(var(--ink) / 0.55)",
                  }}
                >
                  {/* Gold hairline along the top edge — echoes the chronicle popup. */}
                  <GoldRule intensity="soft" className="absolute left-4 right-4" style={{ top: 0 }} />
                  <div className="flex justify-center pt-3 pb-1">
                    <div className="w-10 h-1 rounded-full" style={{ background: "hsl(43, 50%, 35% / 0.55)" }} />
                  </div>
                  <div className="px-4 py-3 overflow-y-auto max-h-[calc(70vh_-_88px)] supports-[height:100dvh]:max-h-[calc(70dvh_-_88px)]">
                    <Timeline
                      chronicles={filteredChronicles}
                      onChronicleSelect={(c) => { handleChronicleSelect(c); setTimelineOpen(false); }}
                      selectedRegion={null}
                      hoveredRegion={hoveredRegion}
                      onRegionHoverChange={isMobile ? undefined : setHoveredRegion}
                    />
                  </div>
                  {/* Close — matches the region chronicle lists so every
                      home-screen chronicle list dismisses the same way. */}
                  <div className="px-4 pb-4 pt-2" style={{ borderTop: "1px solid hsl(43, 50%, 35% / 0.2)" }}>
                    <button
                      onClick={() => setTimelineOpen(false)}
                      className="w-full py-3 font-display text-[11px] uppercase tracking-[0.2em] text-muted-foreground border border-border/40 rounded-sm hover:text-foreground transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </motion.div>
              </>
            ) : (
              <motion.div data-tutorial="timeline-panel"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="relative z-20 overflow-hidden min-h-0"
                style={{
                  background: "var(--surface-parchment-elevated)",
                  borderTop: "1px solid hsl(43, 50%, 35% / 0.4)",
                  boxShadow: "inset 0 1px 0 hsl(43, 50%, 50% / 0.08), 0 -4px 18px hsl(var(--ink) / 0.4)",
                }}
              >
                {/* Gold hairline running across the seam where the timeline meets the map. */}
                <GoldRule intensity="soft" className="absolute left-6 right-6" style={{ top: 0 }} />
                {/* On a short screen (a phone held sideways, a small laptop) the
                    panel is squeezed below its content: it scrolls, so the
                    rows and the Close button stay within reach. */}
                <div className="px-6 py-4 h-full overflow-y-auto overscroll-contain">
                  <Timeline
                    chronicles={filteredChronicles}
                    onChronicleSelect={handleChronicleSelect}
                    selectedRegion={null}
                    hoveredRegion={hoveredRegion}
                    onRegionHoverChange={isMobile ? undefined : setHoveredRegion}
                  />
                  {/* Close — matches the region chronicle lists so every
                      home-screen chronicle list dismisses the same way. */}
                  <div className="pt-3 px-2">
                    <button
                      onClick={() => setTimelineOpen(false)}
                      className="w-full py-2 font-display text-[11px] uppercase tracking-[0.2em] text-muted-foreground border border-border/40 rounded-sm hover:text-foreground transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </>
        )}
      </AnimatePresence>

      {/* Lazy-mounted modal panels. Suspense fallback is `null` — the
          header buttons that open these already give visual feedback on
          click, and each chunk loads in ≤1 frame on any modern
          connection. A spinner inside an empty modal would feel worse
          than a tiny delay before the modal paints. */}
      <Suspense fallback={null}>
        {/* Achievement Showcase */}
        <AchievementShowcase
          open={achievementsOpen}
          onClose={() => setAchievementsOpen(false)}
          achievements={achievementProgress}
        />

        {/* Archivist's Ledger */}
        <ArchivistLedger open={ledgerOpen} onClose={() => setLedgerOpen(false)} />

        {/* Settings Panel */}
        <SettingsPanel open={settingsOpen} focus={settingsFocus} onClose={() => { setSettingsOpen(false); setSettingsFocus(undefined); }} />
      </Suspense>
    </div>
  );
};

export default Index;
